/**
 * The locked backup file (part 41b log, principles BP1–BP9, "Format, version 1", tables BW and E): a book's bundle sealed
 * under the writer's backup passphrase. Argon2id (KDF version 1) turns the passphrase and the file's salt into K, kept as
 * a non-extractable HKDF key; each file has a random nonce, and HKDF(K, nonce) gives that file's header check key and
 * chunk key (BP6). The header is authenticated by its check, each 1 MiB chunk by AES-256-GCM with the whole header, its
 * index and the last-chunk flag as associated data (BP4). A file is read chunk by chunk, so only the output is ever held
 * whole. WebCrypto, randomness and Argon2id are injected; it runs in a worker, never on the keystroke path.
 */
import { checkParameters, KDF_DEFAULT, KDF_V1, newKdfRecord } from './kdf.ts';
import type { KdfParameters, KdfRecord } from './kdf.ts';
import { KdfError } from './login.ts';
import type { Argon2id } from './login.ts';
import { passwordBytes } from './password.ts';

const encoder = new TextEncoder();

/** BP9: the first 8 bytes of every locked backup, ASCII `CTBACKUP`. */
export const BACKUP_MAGIC: Uint8Array<ArrayBuffer> = encoder.encode('CTBACKUP');

/** BP9: the format version this build writes and reads; a higher one is "made by a newer version". */
export const BACKUP_FORMAT_V1 = 1;

/** Format v1: the header's length, check included. */
export const BACKUP_HEADER_BYTES = 110;

/** Format v1: the plaintext of every chunk but the last; a header naming another size is malformed. */
export const BACKUP_CHUNK_BYTES = 1_048_576;

/** Format v1: the most plaintext a file may hold (16 TiB); a header naming more is refused as too large (E13). */
export const BACKUP_MAX_PLAIN_BYTES = 2 ** 44;

/** The file nonce's length: 256 random bits per file (BP6). */
const NONCE_BYTES = 32;

/** AES-GCM's tag, which every chunk carries. */
const TAG_BYTES = 16;

/** AES-GCM's IV: 4 zero bytes, then the chunk's index (8). */
const IV_BYTES = 12;

/** The header check's length: an HMAC-SHA-256. */
const CHECK_BYTES = 32;

/** The header's bytes the check covers: all before it. */
const CHECKED_BYTES = BACKUP_HEADER_BYTES - CHECK_BYTES;

/** The device's check value's length (BP2). */
const CHECK_VALUE_BYTES = 32;

/** Where each header field starts (format v1). */
const AT = { format: 8, kdfVersion: 9, memoryKiB: 10, passes: 14, salt: 18, nonce: 34, chunkBytes: 66, plainBytes: 70, check: 78 } as const;

const HEADER_LABEL = encoder.encode('cockatrice/backup/header/v1');
const FILE_LABEL = encoder.encode('cockatrice/backup/file/v1');
const CHECK_LABEL = encoder.encode('cockatrice/backup/check/v1');

/**
 * BP2: the passphrase's key: the KDF record it was derived with (the file's salt and parameters) and K, an HKDF key that
 * can't be extracted. The device keeps this, never the passphrase.
 */
export interface BackupKey {
  readonly kdf: KdfRecord;
  readonly key: CryptoKey;
}

/**
 * E12, E13: why a file was refused. `not-backup`: no magic. `newer`: a format or KDF version this build doesn't know
 * (BP9). `malformed`: anything else wrong with the header, parameters outside the floor or ceiling included (BP5).
 * `too-large`: a plaintext length over {@link BACKUP_MAX_PLAIN_BYTES}. `wrong-passphrase`: the header check failed (BW7).
 * `damaged`: the size doesn't match the header, or a chunk fails (BW8).
 */
export type BackupFailure = 'not-backup' | 'newer' | 'malformed' | 'too-large' | 'wrong-passphrase' | 'damaged';

/** E12, E13: thrown for a file that can't be opened (or a plaintext too big to seal), with why. */
export class BackupError extends Error {
  override readonly name = 'BackupError';
  readonly reason: BackupFailure;

  constructor(reason: BackupFailure) {
    super(`the backup was refused: ${reason}`);
    this.reason = reason;
  }
}

/**
 * Format v1: a header as read, before its check is verified (that needs the key): the KDF record, the file nonce, the
 * plaintext length and all {@link BACKUP_HEADER_BYTES} bytes, which every chunk is bound to (BP4).
 */
export interface BackupHeader {
  readonly kdf: KdfRecord;
  readonly nonce: Uint8Array<ArrayBuffer>;
  readonly plainBytes: number;
  readonly bytes: Uint8Array<ArrayBuffer>;
}

/** `parts` joined, in order. */
function joined(...parts: readonly Uint8Array[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(parts.reduce((length, part) => length + part.length, 0));
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}

/** `value` as four bytes, big-endian. */
function u32(value: number): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(4);
  new DataView(out.buffer).setUint32(0, value);
  return out;
}

/** `value` (a whole number up to 2⁵³) as eight bytes, big-endian. */
function u64(value: number): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(8);
  const view = new DataView(out.buffer);
  view.setUint32(0, Math.floor(value / 2 ** 32));
  view.setUint32(4, value % 2 ** 32);
  return out;
}

/** How many chunks a plaintext of `plainBytes` goes in: one empty chunk for none. */
function chunksFor(plainBytes: number): number {
  return Math.max(1, Math.ceil(plainBytes / BACKUP_CHUNK_BYTES));
}

/** Chunk `index`'s IV: 4 zero bytes and the index; the key is the file's own (BP6), so no IV repeats under it. */
function chunkIv(index: number): Uint8Array<ArrayBuffer> {
  return joined(new Uint8Array(IV_BYTES - 8), u64(index));
}

/** BP4: what chunk `index` is authenticated with: the whole header, its index and whether it is the last. */
function chunkData(header: Uint8Array, index: number, last: boolean): Uint8Array<ArrayBuffer> {
  return joined(header, u64(index), Uint8Array.of(last ? 1 : 0));
}

/** BP6: a file's two keys, from K with the file's nonce as HKDF's salt: the header check's and the chunks'. */
async function fileKeys(subtle: SubtleCrypto, key: BackupKey, nonce: Uint8Array<ArrayBuffer>): Promise<{ readonly check: CryptoKey; readonly chunks: CryptoKey }> {
  const check = await subtle.deriveKey({ name: 'HKDF', hash: 'SHA-256', salt: nonce, info: HEADER_LABEL }, key.key, { name: 'HMAC', hash: 'SHA-256', length: 256 }, false, ['sign', 'verify']);
  const chunks = await subtle.deriveKey({ name: 'HKDF', hash: 'SHA-256', salt: nonce, info: FILE_LABEL }, key.key, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  return { check, chunks };
}

/**
 * BP2, BP8: a KDF record for a new passphrase (set or changed): KDF version 1, a fresh salt from `random`
 * (`crypto.getRandomValues`) and `parameters`, {@link KDF_DEFAULT} unless given.
 */
export function newBackupKdf(random: (bytes: Uint8Array<ArrayBuffer>) => Uint8Array, parameters: KdfParameters = KDF_DEFAULT): KdfRecord {
  return newKdfRecord(random, parameters);
}

/**
 * BP2, BP5: K for `passphrase` under `kdf`: Argon2id on the NFC passphrase's bytes ({@link passwordBytes}) with the
 * record's salt and parameters, parallelism 1, 32 bytes, imported as an HKDF key that can't be extracted. Parameters
 * outside the floor or the ceiling, or a salt that isn't 16 bytes, throw {@link BackupError} `malformed` before Argon2id
 * runs (BW7: a planted file can't weaken the key or exhaust memory). An empty passphrase, or one with a lone surrogate,
 * throws `PasswordError`; an Argon2id output of the wrong length throws `KdfError` (`output`); Argon2id's own failures
 * pass through. The passphrase's bytes and Argon2id's output are zeroed whatever happens.
 */
export async function deriveBackupKey(passphrase: string, kdf: KdfRecord, deps: { readonly subtle: SubtleCrypto; readonly argon2id: Argon2id }): Promise<BackupKey> {
  if (checkParameters(kdf, null).kind === 'refused' || kdf.salt.length !== KDF_V1.saltBytes) throw new BackupError('malformed');
  const bytes = passwordBytes(passphrase);
  let output: Uint8Array<ArrayBuffer> | null = null;
  try {
    output = await deps.argon2id({ password: bytes, salt: kdf.salt, memoryKiB: kdf.memoryKiB, passes: kdf.passes, parallelism: KDF_V1.parallelism, outputBytes: KDF_V1.outputBytes });
    if (output.length !== KDF_V1.outputBytes) throw new KdfError('output');
    const key = await deps.subtle.importKey('raw', output, 'HKDF', false, ['deriveBits', 'deriveKey']);
    return { kdf: { version: kdf.version, salt: kdf.salt.slice(), memoryKiB: kdf.memoryKiB, passes: kdf.passes }, key };
  } finally {
    bytes.fill(0);
    output?.fill(0);
  }
}

/**
 * BP2, BP7 (E15, E16): the device's check value for `key`: 32 bytes of HKDF(K, no salt, `cockatrice/backup/check/v1`).
 * Kept beside the key; the passphrase check derives it again from what the writer types and compares with
 * {@link sameCheckValue}. It says nothing of K or of any file's keys (another label).
 */
export async function backupCheckValue(key: BackupKey, subtle: SubtleCrypto): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(await subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(0), info: CHECK_LABEL }, key.key, CHECK_VALUE_BYTES * 8));
}

/**
 * BP7 (E15, E16): whether two check values are the same, comparing every byte whatever the first difference, so the time
 * taken doesn't say where they differ. Values of different lengths are never the same.
 */
export function sameCheckValue(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  // The lengths are equal, so `b[i]` is always a byte.
  return a.reduce((difference, byte, i) => difference | (byte ^ Number(b[i])), 0) === 0;
}

/** BP9, E11/E14: whether `head` (a file's first bytes) starts with {@link BACKUP_MAGIC}: a locked backup, not a `.zip`. */
export function isLockedBackup(head: Uint8Array): boolean {
  return head.length >= BACKUP_MAGIC.length && BACKUP_MAGIC.every((byte, i) => head[i] === byte);
}

/** BP9: throws unless `version` (a header's version byte, missing when cut short) is `known`: `newer` above it, `malformed` otherwise. */
function versionOf(version: number | undefined, known: number): void {
  if (version === undefined) throw new BackupError('malformed');
  if (version > known) throw new BackupError('newer');
  if (version !== known) throw new BackupError('malformed');
}

/**
 * The boundary parser of a locked file's header (BP4, BP5, BP9; E13), given at least its first
 * {@link BACKUP_HEADER_BYTES} bytes (only those are read). Throws {@link BackupError}, checked in this order:
 * `not-backup` without the magic; the format version, then the KDF version: `newer` above 1, `malformed` at 0 or cut
 * off; `malformed` for fewer bytes than a header, a chunk size other than {@link BACKUP_CHUNK_BYTES}, or parameters outside the floor or the ceiling
 * (table K, with no highest seen: each file stands alone, BP8); `too-large` for a plaintext length over
 * {@link BACKUP_MAX_PLAIN_BYTES}. The header check is not verified here (that needs the key: {@link openBackup}).
 * Everything returned is copied, so the caller's buffer can't change it later.
 */
export function readBackupHeader(bytes: Uint8Array): BackupHeader {
  if (!isLockedBackup(bytes)) throw new BackupError('not-backup');
  versionOf(bytes[AT.format], BACKUP_FORMAT_V1);
  versionOf(bytes[AT.kdfVersion], KDF_V1.version);
  if (bytes.length < BACKUP_HEADER_BYTES) throw new BackupError('malformed');
  const header = bytes.slice(0, BACKUP_HEADER_BYTES);
  const view = new DataView(header.buffer);
  const parameters = { memoryKiB: view.getUint32(AT.memoryKiB), passes: view.getUint32(AT.passes) };
  if (view.getUint32(AT.chunkBytes) !== BACKUP_CHUNK_BYTES || checkParameters(parameters, null).kind === 'refused') throw new BackupError('malformed');
  const plainBytes = view.getUint32(AT.plainBytes) * 2 ** 32 + view.getUint32(AT.plainBytes + 4);
  if (plainBytes > BACKUP_MAX_PLAIN_BYTES) throw new BackupError('too-large');
  return {
    kdf: { version: KDF_V1.version, salt: header.slice(AT.salt, AT.nonce), ...parameters },
    nonce: header.slice(AT.nonce, AT.chunkBytes),
    plainBytes,
    bytes: header,
  };
}

/**
 * BP4, BP6, E2/E8: `plain` sealed under `key` in format v1: a fresh nonce from `random`, the header and its check, then
 * each chunk of up to {@link BACKUP_CHUNK_BYTES} sealed with its index and last flag (one empty chunk for an empty
 * plaintext). Reads `plain` a chunk at a time. Throws {@link BackupError} `too-large` past {@link BACKUP_MAX_PLAIN_BYTES},
 * and `RangeError` when `random` doesn't fill 32 bytes (a caller's mistake). BP3's open-back is the caller's.
 */
export async function sealBackup(plain: Blob, key: BackupKey, deps: { readonly subtle: SubtleCrypto; readonly random: (bytes: Uint8Array<ArrayBuffer>) => Uint8Array }): Promise<Blob> {
  const plainBytes = plain.size;
  if (plainBytes > BACKUP_MAX_PLAIN_BYTES) throw new BackupError('too-large');
  const nonce = deps.random(new Uint8Array(NONCE_BYTES)).slice();
  if (nonce.length !== NONCE_BYTES) throw new RangeError(`a backup nonce must be ${String(NONCE_BYTES)} bytes`);
  const { kdf } = key;
  const checked = joined(BACKUP_MAGIC, Uint8Array.of(BACKUP_FORMAT_V1, kdf.version), u32(kdf.memoryKiB), u32(kdf.passes), kdf.salt, nonce, u32(BACKUP_CHUNK_BYTES), u64(plainBytes));
  const keys = await fileKeys(deps.subtle, key, nonce);
  const header = joined(checked, new Uint8Array(await deps.subtle.sign('HMAC', keys.check, checked)));
  const chunks = chunksFor(plainBytes);
  const parts: BlobPart[] = [header];
  for (let index = 0; index < chunks; index += 1) {
    const chunk = await plain.slice(index * BACKUP_CHUNK_BYTES, (index + 1) * BACKUP_CHUNK_BYTES).arrayBuffer();
    parts.push(await deps.subtle.encrypt({ name: 'AES-GCM', iv: chunkIv(index), additionalData: chunkData(header, index, index === chunks - 1) }, keys.chunks, chunk));
  }
  return new Blob(parts);
}

/**
 * BP4, BP5, E11–E13: the plaintext of the locked file `sealed`. Reads the header ({@link readBackupHeader}'s refusals),
 * then checks the file's size is exactly the header and its chunks (else `damaged`, before any key is asked for, so a
 * truncated or padded file never costs the writer a passphrase); then asks `keyFor` for the key of the header's KDF
 * record (it may prompt and run Argon2id; any rejection, a {@link BackupError} or another, passes through unchanged);
 * verifies the header check (`wrong-passphrase` when it fails, BW7); then opens chunk by chunk through `Blob.slice`, each
 * failure `damaged` (BW8). `onRead`, when given, is called after each chunk with the sealed bytes read so far (header
 * included), ending at `sealed.size`, for progress. Only the output is ever held whole.
 */
export async function openBackup(sealed: Blob, keyFor: (kdf: KdfRecord) => Promise<BackupKey>, subtle: SubtleCrypto, onRead?: (bytes: number) => void): Promise<Blob> {
  const header = readBackupHeader(new Uint8Array(await sealed.slice(0, BACKUP_HEADER_BYTES).arrayBuffer()));
  const chunks = chunksFor(header.plainBytes);
  if (sealed.size !== BACKUP_HEADER_BYTES + header.plainBytes + chunks * TAG_BYTES) throw new BackupError('damaged');
  const keys = await fileKeys(subtle, await keyFor(header.kdf), header.nonce);
  const check = header.bytes.slice(AT.check);
  if (!(await subtle.verify('HMAC', keys.check, check, header.bytes.slice(0, CHECKED_BYTES)))) throw new BackupError('wrong-passphrase');
  const parts: ArrayBuffer[] = [];
  let at = BACKUP_HEADER_BYTES;
  for (let index = 0; index < chunks; index += 1) {
    const last = index === chunks - 1;
    const length = (last ? header.plainBytes - index * BACKUP_CHUNK_BYTES : BACKUP_CHUNK_BYTES) + TAG_BYTES;
    const bytes = await sealed.slice(at, at + length).arrayBuffer();
    try {
      parts.push(await subtle.decrypt({ name: 'AES-GCM', iv: chunkIv(index), additionalData: chunkData(header.bytes, index, last) }, keys.chunks, bytes));
    } catch {
      throw new BackupError('damaged');
    }
    at += length;
    onRead?.(at);
  }
  return new Blob(parts);
}
